import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  rectSortingStrategy,
  verticalListSortingStrategy,
  arrayMove,
  sortableKeyboardCoordinates,
} from "@dnd-kit/sortable";
import { toast } from "sonner";
import { Search, LayoutGrid, List as ListIcon, X, AlertTriangle, RefreshCw } from "lucide-react";
import { http, formatApiErrorDetail } from "../lib/api";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { CategoryFilter } from "../components/CategoryFilter";
import { PdfItem } from "../components/PdfItem";
import { SortablePdfItem } from "../components/SortablePdfItem";
import { PdfPreviewBlock } from "../components/PdfPreviewBlock";
import { PopularRail } from "../components/PopularRail";
import { EmptyState } from "../components/EmptyState";
import { HeroBackground } from "../components/HeroBackground";
import { useCategories } from "../context/CategoriesContext";
import { useAuth } from "../context/AuthContext";
import { cn } from "../lib/utils";

// Resolve a `?category=` URL value to the canonical category name.
// Accepts an exact name (case-insensitive) or a slug. Returns "All" if
// the value is empty / "all" / not found.
function resolveCategoryFromParam(value, categories) {
  if (!value) return "All";
  const normalized = value.trim().toLowerCase();
  if (!normalized || normalized === "all") return "All";
  const match = categories.find(
    (c) => c.name.toLowerCase() === normalized || c.slug.toLowerCase() === normalized
  );
  return match ? match.name : "All";
}

export default function PdfLibrary() {
  const [searchParams, setSearchParams] = useSearchParams();
  const embedMode = searchParams.get("embed") === "1";
  const categoryParam = searchParams.get("category");
  const { categories } = useCategories();
  const { user } = useAuth();
  const isAdmin = user && user.role === "admin";
  const [pdfs, setPdfs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [activeCategory, setActiveCategory] = useState("All");
  const [view, setView] = useState("list"); // default list per user
  const [expandedId, setExpandedId] = useState(null);
  const [loadError, setLoadError] = useState("");

  // Sync activeCategory whenever the URL `?category=` value or the loaded
  // categories list changes. We need both because the param may arrive before
  // categories have loaded from the API on first paint.
  useEffect(() => {
    if (!categories.length && categoryParam) return; // wait for categories
    setActiveCategory(resolveCategoryFromParam(categoryParam, categories));
  }, [categoryParam, categories]);

  const handleCategoryChange = useCallback(
    (name) => {
      setActiveCategory(name);
      // Update the URL so the choice is shareable / linkable.
      // We mutate only the `category` key — all other params (embed, etc.) are preserved.
      const next = new URLSearchParams(searchParams);
      if (!name || name === "All") {
        next.delete("category");
      } else {
        const cat = categories.find((c) => c.name === name);
        next.set("category", cat ? cat.slug : name.toLowerCase());
      }
      setSearchParams(next, { replace: true });
    },
    [categories, searchParams, setSearchParams]
  );

  const loadPdfs = useCallback(async ({ retries = 1 } = {}) => {
    setLoadError("");
    let attempt = 0;
    // Try up to (retries + 1) times. Brief backoff between attempts handles
    // cold starts and transient network blips silently.
    while (true) {
      try {
        const { data } = await http.get("/pdfs");
        setPdfs(data);
        setLoading(false);
        return;
      } catch (e) {
        attempt += 1;
        if (attempt > retries) {
          const detail = formatApiErrorDetail(e.response?.data?.detail);
          setLoadError(detail || "Couldn't load the library. Check your connection and retry.");
          setLoading(false);
          return;
        }
        await new Promise((r) => setTimeout(r, 800 * attempt));
      }
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (cancelled) return;
      await loadPdfs({ retries: 2 });
    })();
    return () => {
      cancelled = true;
    };
  }, [loadPdfs]);

  const counts = useMemo(() => {
    const c = { All: pdfs.length };
    for (const cat of categories) c[cat.name] = 0;
    for (const p of pdfs) c[p.folder] = (c[p.folder] || 0) + 1;
    return c;
  }, [pdfs, categories]);

  const featured = useMemo(() => pdfs.filter((p) => p.featured), [pdfs]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return pdfs.filter((p) => {
      const matchesCat = activeCategory === "All" || p.folder === activeCategory;
      if (!matchesCat) return false;
      if (!q) return true;
      return (
        p.title.toLowerCase().includes(q) ||
        (p.description || "").toLowerCase().includes(q) ||
        p.folder.toLowerCase().includes(q)
      );
    });
  }, [pdfs, query, activeCategory]);

  const handleToggleExpand = (id) => {
    setExpandedId((cur) => (cur === id ? null : id));
  };

  // Drag-and-drop reorder is admin-only, disabled in embed mode and
  // only when no filter is active (otherwise indices would be ambiguous).
  const reorderEnabled =
    isAdmin && !embedMode && activeCategory === "All" && !query.trim();

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = pdfs.findIndex((p) => p.id === active.id);
    const newIndex = pdfs.findIndex((p) => p.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;
    const previous = pdfs;
    const reordered = arrayMove(pdfs, oldIndex, newIndex);
    setPdfs(reordered); // optimistic
    try {
      await http.post("/pdfs/reorder", { order: reordered.map((p) => p.id) });
      toast.dismiss();
      toast.success("Order saved");
    } catch (e) {
      setPdfs(previous);
      toast.dismiss();
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not save order");
    }
  };

  const filteredIds = useMemo(() => filtered.map((p) => p.id), [filtered]);

  return (
    <div className={cn("max-w-6xl mx-auto px-4 sm:px-6 lg:px-8", embedMode ? "py-6" : "py-10 sm:py-14")}>
      {/* Hero — hidden in embed mode */}
      {!embedMode && (
        <section className="relative isolate mb-10 -mx-4 sm:-mx-6 lg:-mx-8 px-4 sm:px-6 lg:px-8 pt-8 pb-12" data-testid="hero-section">
          <HeroBackground />
          <div className="relative z-10">
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight text-slate-900 leading-[1.05]">
              Your PDFPLAYS,<br />
              <span className="text-brand-primary">Where You Need It!!</span>
            </h1>
            <p className="mt-5 text-base sm:text-lg text-slate-600 max-w-2xl leading-relaxed">
              Browse Drills &amp; Skills, Playbooks, Training Guides.
              <br />
              Open, Link, Embed, Preview Any PDF.
            </p>
          </div>
        </section>
      )}

      {/* Most Viewed This Week — only on the default 'All' view (matches Featured gating) */}
      {activeCategory === "All" && !query.trim() && (
        <PopularRail
          expandedId={expandedId}
          onToggleExpand={handleToggleExpand}
        />
      )}

      {/* Featured — only in the default 'All' view */}
      {featured.length > 0 && activeCategory === "All" && !query.trim() && (
        <section className="mb-10" data-testid="featured-section">
          <div className="flex items-end justify-between mb-4">
            <div>
              <h2 className="font-display text-2xl sm:text-3xl font-bold text-slate-900">Featured</h2>
              <p className="text-sm text-slate-500 mt-1">Hand-picked playbooks to get you started.</p>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5 stagger">
            {featured.slice(0, 3).map((pdf) => (
              <PdfItem
                key={`feat-${pdf.id}`}
                pdf={pdf}
                view="grid"
                compact
                renderInlinePreview={false}
                expanded={expandedId === pdf.id}
                onToggleExpand={() => handleToggleExpand(pdf.id)}
              />
            ))}
          </div>
          {/* Full-width preview rendered below the featured row */}
          {featured.slice(0, 3).some((p) => p.id === expandedId) && (
            <div className="mt-5">
              <PdfPreviewBlock
                pdf={featured.find((p) => p.id === expandedId)}
                fullWidth
                onClose={() => setExpandedId(null)}
              />
            </div>
          )}
        </section>
      )}

      {/* Controls */}
      <section className="mb-6 space-y-5" data-testid="library-controls">
        <div className="relative max-w-2xl">
          <Search className="absolute left-5 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400 pointer-events-none" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by title, description, or sport"
            className={cn(
              "pl-14 pr-12 h-14 rounded-full bg-white border border-slate-200/80 text-base",
              "shadow-[0_10px_30px_-12px_rgba(15,23,42,0.12)]",
              "transition-shadow duration-200 ease-out",
              "hover:shadow-[0_14px_36px_-14px_rgba(15,23,42,0.18)]",
              "focus-visible:shadow-[0_18px_44px_-18px_rgba(14,165,233,0.35)]",
              "focus-visible:border-brand-primary focus-visible:ring-0"
            )}
            data-testid="search-input"
          />
          {query && (
            <button
              type="button"
              onClick={() => setQuery("")}
              aria-label="Clear search"
              data-testid="search-clear"
              className="absolute right-4 top-1/2 -translate-y-1/2 h-8 w-8 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-500 hover:text-slate-700 flex items-center justify-center transition-colors"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>

        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="min-w-0 flex-1">
            <CategoryFilter
              categories={categories}
              activeCategory={activeCategory}
              onChange={handleCategoryChange}
              counts={counts}
            />
          </div>
          <div className="inline-flex shrink-0 self-start sm:self-auto rounded-full border border-slate-200 bg-white p-1 shadow-sm" data-testid="view-toggle">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setView("list")}
              className={cn(
                "h-8 px-3 rounded-full gap-1.5 text-slate-500 transition-all",
                view === "list" && "bg-slate-900 text-white hover:bg-slate-900 hover:text-white"
              )}
              data-testid="view-toggle-list"
            >
              <ListIcon className="h-4 w-4" />
              List
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setView("grid")}
              className={cn(
                "h-8 px-3 rounded-full gap-1.5 text-slate-500 transition-all",
                view === "grid" && "bg-slate-900 text-white hover:bg-slate-900 hover:text-white"
              )}
              data-testid="view-toggle-grid"
            >
              <LayoutGrid className="h-4 w-4" />
              Grid
            </Button>
          </div>
        </div>

        {isAdmin && !embedMode && (
          <div
            className="text-xs text-slate-500 flex items-center gap-2"
            data-testid="admin-reorder-hint"
          >
            <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full bg-brand-accent-soft text-brand-primary font-semibold">
              Admin
            </span>
            {reorderEnabled
              ? "Drag any card by the grip handle (top-right on hover) to reorder."
              : "Clear filters and search to enable drag-to-reorder."}
          </div>
        )}
      </section>

      {/* List/Grid */}
      <section data-testid="pdf-list-section">
        {loading ? (
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-28 rounded-2xl bg-white border border-slate-200 animate-pulse" />
            ))}
          </div>
        ) : loadError ? (
          <div
            className="rounded-2xl border border-amber-200 bg-amber-50 px-5 py-4 flex flex-col sm:flex-row sm:items-center gap-3"
            data-testid="library-load-error"
          >
            <div className="flex items-start gap-3 flex-1">
              <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5 shrink-0" />
              <div className="text-sm text-amber-900 leading-relaxed">
                <div className="font-semibold">Couldn't load the library</div>
                <div className="text-amber-800/90">{loadError}</div>
              </div>
            </div>
            <Button
              onClick={loadPdfs}
              size="sm"
              variant="outline"
              className="bg-white shrink-0"
              data-testid="library-retry-btn"
            >
              <RefreshCw className="h-4 w-4" />
              Retry
            </Button>
          </div>
        ) : filtered.length === 0 ? (
          <EmptyState />
        ) : view === "grid" ? (
          <>
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext items={filteredIds} strategy={rectSortingStrategy}>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 stagger" data-testid="pdf-grid">
                  {filtered.map((pdf) => (
                    <SortablePdfItem
                      key={pdf.id}
                      pdf={pdf}
                      view="grid"
                      compact
                      renderInlinePreview={false}
                      expanded={expandedId === pdf.id}
                      onToggleExpand={() => handleToggleExpand(pdf.id)}
                      draggable={reorderEnabled}
                    />
                  ))}
                </div>
              </SortableContext>
            </DndContext>
            {/* Full-width preview rendered below the grid (only if expanded item is in this list) */}
            {filtered.some((p) => p.id === expandedId) && (
              <div className="mt-5">
                <PdfPreviewBlock
                  pdf={filtered.find((p) => p.id === expandedId)}
                  fullWidth
                  onClose={() => setExpandedId(null)}
                />
              </div>
            )}
          </>
        ) : (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext items={filteredIds} strategy={verticalListSortingStrategy}>
              <div className="space-y-4 stagger" data-testid="pdf-list">
                {filtered.map((pdf) => (
                  <SortablePdfItem
                    key={pdf.id}
                    pdf={pdf}
                    view="list"
                    expanded={expandedId === pdf.id}
                    onToggleExpand={() => handleToggleExpand(pdf.id)}
                    draggable={reorderEnabled}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        )}
      </section>
    </div>
  );
}
